import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, Plus, Trash2, Play, Pause, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoTimecodeEditor = ({ gameId, panoramas, getAuthHeader, externalVideoUrl }) => {
  const [timecodes, setTimecodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedPanorama, setSelectedPanorama] = useState(null);
  const [hlsReady, setHlsReady] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  // Load timecodes
  useEffect(() => {
    const loadTimecodes = async () => {
      try {
        const response = await axios.get(`${API}/games/${gameId}/video/timecodes`);
        setTimecodes(response.data || []);
      } catch (error) {
        console.error('Error loading timecodes:', error);
      }
    };
    loadTimecodes();
  }, [gameId]);

  // Check HLS status (only for uploaded videos)
  useEffect(() => {
    if (externalVideoUrl) {
      setVideoReady(true);
      return;
    }
    const checkHlsStatus = async () => {
      try {
        const response = await axios.get(`${API}/games/${gameId}/video/status`);
        setHlsReady(response.data.hls_ready);
        setVideoReady(response.data.hls_ready);
      } catch (error) {
        console.error('Error checking video status:', error);
      }
    };
    checkHlsStatus();
  }, [gameId, externalVideoUrl]);

  // Initialize video player
  useEffect(() => {
    if (!videoReady || !videoRef.current) return;

    const video = videoRef.current;
    
    // External video - use direct URL
    if (externalVideoUrl) {
      video.src = externalVideoUrl;
      return;
    }
    
    // HLS video
    const hlsUrl = `${API}/games/${gameId}/video/hls/master.m3u8`;

    // Native HLS support (Safari)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      return;
    }

    // Use hls.js
    if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
      });
      
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(window.Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case window.Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case window.Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });
      
      hlsRef.current = hls;
      
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }
  }, [videoReady, gameId]);

  // Video event handlers - depend on videoReady so handlers attach after <video> renders
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [videoReady]);

  // Add timecode
  const handleAddTimecode = async () => {
    if (!selectedPanorama) {
      alert('Выберите панораму');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/games/${gameId}/video/timecodes`,
        {
          time: currentTime,
          panorama_id: selectedPanorama,
        },
        { headers: getAuthHeader() }
      );
      
      setTimecodes(prev => [...prev, response.data].sort((a, b) => a.time - b.time));
      setSelectedPanorama(null);
    } catch (error) {
      console.error('Error adding timecode:', error);
      alert('Ошибка добавления таймкода');
    } finally {
      setSaving(false);
    }
  };

  // Delete timecode
  const handleDeleteTimecode = async (timecodeId) => {
    if (!window.confirm('Удалить этот таймкод?')) return;

    try {
      await axios.delete(
        `${API}/games/${gameId}/video/timecodes/${timecodeId}`,
        { headers: getAuthHeader() }
      );
      setTimecodes(prev => prev.filter(tc => tc.id !== timecodeId));
    } catch (error) {
      console.error('Error deleting timecode:', error);
      alert('Ошибка удаления таймкода');
    }
  };

  // Format time as mm:ss
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get panorama preview URL
  const getPanoramaPreview = (panoramaId) => {
    const panorama = panoramas.find(p => p.id === panoramaId);
    if (!panorama) return null;
    
    if (panorama.custom_preview_path) {
      return `${API}/panoramas/${panorama.id}/custom-preview?t=${Date.now()}`;
    }
    return `${API}/panoramas/${panorama.id}/preview`;
  };

  // Get panorama name
  const getPanoramaName = (panoramaId) => {
    const panorama = panoramas.find(p => p.id === panoramaId);
    return panorama?.name || `Панорама ${panoramaId.slice(0, 8)}`;
  };

  if (!videoReady) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        <p>Видео не готово или обрабатывается</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
        <Clock size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
        Редактор таймкодов интерактивного видео
      </h3>

      {/* Video Player */}
      <div style={{ marginBottom: '1rem' }}>
        <video
          ref={videoRef}
          style={{
            width: '100%',
            maxHeight: '300px',
            background: '#000',
            borderRadius: '8px',
          }}
          controls={false}
          playsInline
        />
        
        {/* Custom controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginTop: '0.5rem',
          padding: '0.5rem',
          background: '#e9ecef',
          borderRadius: '6px',
        }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (isPlaying) {
                videoRef.current?.pause();
              } else {
                videoRef.current?.play();
              }
            }}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </Button>
          
          {/* Progress bar */}
          <div
            style={{
              flex: 1,
              height: '8px',
              background: '#dee2e6',
              borderRadius: '4px',
              cursor: 'pointer',
              position: 'relative',
            }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percent = x / rect.width;
              if (videoRef.current) {
                videoRef.current.currentTime = percent * duration;
              }
            }}
          >
            {/* Progress fill */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                background: '#3b82f6',
                borderRadius: '4px',
              }}
            />
            
            {/* Timecode markers */}
            {timecodes.map((tc, idx) => (
              <div
                key={tc.id || idx}
                style={{
                  position: 'absolute',
                  left: `${duration > 0 ? (tc.time / duration) * 100 : 0}%`,
                  top: '-2px',
                  bottom: '-2px',
                  width: '6px',
                  marginLeft: '-3px',
                  background: '#f59e0b',
                  borderRadius: '2px',
                  cursor: 'pointer',
                }}
                title={`${formatTime(tc.time)} - ${getPanoramaName(tc.panorama_id)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoRef.current) {
                    videoRef.current.currentTime = tc.time;
                  }
                }}
              />
            ))}
          </div>
          
          <span style={{ fontSize: '0.85rem', fontFamily: 'monospace', minWidth: '90px' }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Add Timecode Section */}
      <div style={{
        padding: '1rem',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        marginBottom: '1rem',
      }}>
        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#495057' }}>
          Поставьте видео на паузу в нужный момент и выберите панораму:
        </p>
        
        {/* Panorama grid for selection */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap: '0.5rem',
          maxHeight: '200px',
          overflowY: 'auto',
          marginBottom: '0.75rem',
        }}>
          {panoramas.map((panorama, idx) => (
            <div
              key={panorama.id}
              onClick={() => setSelectedPanorama(panorama.id)}
              style={{
                aspectRatio: '16/9',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: selectedPanorama === panorama.id ? '3px solid #3b82f6' : '2px solid transparent',
                position: 'relative',
              }}
            >
              <img
                src={getPanoramaPreview(panorama.id)}
                alt={panorama.name || `Панорама ${idx + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                padding: '0.25rem',
                fontSize: '0.7rem',
                color: 'white',
                textAlign: 'center',
              }}>
                {idx + 1}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button
            onClick={handleAddTimecode}
            disabled={!selectedPanorama || saving}
            style={{ flex: 1 }}
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Plus size={16} />
            )}
            <span style={{ marginLeft: '0.5rem' }}>
              Добавить таймкод в {formatTime(currentTime)}
            </span>
          </Button>
        </div>
      </div>

      {/* Timecodes List */}
      <div>
        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: '500' }}>
          Таймкоды ({timecodes.length})
        </h4>
        
        {timecodes.length === 0 ? (
          <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Таймкоды не добавлены. Поставьте видео на паузу и выберите панораму.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {timecodes.map((tc, idx) => (
              <div
                key={tc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6',
                }}
              >
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#3b82f6',
                  minWidth: '50px',
                }}>
                  {formatTime(tc.time)}
                </span>
                
                <div style={{
                  width: '60px',
                  height: '34px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}>
                  <img
                    src={getPanoramaPreview(tc.panorama_id)}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                
                <span style={{ flex: 1, fontSize: '0.85rem', color: '#495057' }}>
                  {getPanoramaName(tc.panorama_id)}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (videoRef.current) {
                      videoRef.current.currentTime = tc.time;
                    }
                  }}
                  title="Перейти к таймкоду"
                >
                  <Play size={14} />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteTimecode(tc.id)}
                  style={{ color: '#dc2626', borderColor: '#dc2626' }}
                  title="Удалить таймкод"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoTimecodeEditor;
